"use strict";
// Copyright 2022 Fergal Daly <fergald@gmail.com>.
Object.defineProperty(exports, "__esModule", { value: true });
exports.SimpleDate = exports.Timezone = exports.WEEK_START = exports.ONE_DAY_MS = exports.ONE_HOUR_MS = exports.ONE_MINUTE_MS = void 0;
exports.getSecondsFromTime = getSecondsFromTime;
exports.compareNumbers = compareNumbers;
exports.getWeekStart = getWeekStart;
exports.limitsOfMonth = limitsOfMonth;
exports.daysOfMonth = daysOfMonth;
exports.daysOf = daysOf;
exports.weeksOf = weeksOf;
exports.weeksOfMonth = weeksOfMonth;
exports.nextMonth = nextMonth;
exports.previousMonth = previousMonth;
exports.makeTime = makeTime;
exports.parseTime = parseTime;
exports.parseTimeOrRange = parseTimeOrRange;
exports.parseTimesOrRanges = parseTimesOrRanges;
exports.groupByMonth = groupByMonth;
exports.overlap = overlap;
exports.lastOfMonth = lastOfMonth;
const pad = require("./pad");
exports.ONE_MINUTE_MS = 60 * 1000;
exports.ONE_HOUR_MS = 60 * exports.ONE_MINUTE_MS;
exports.ONE_DAY_MS = 24 * exports.ONE_HOUR_MS;
exports.WEEK_START = 1; // Monday
const GMT_OFFSET_STRING = '+00:00';
function msFromParts(year, month, day, offsetString, hour = 0, minute = 0, second = 0) {
    const dateString = `${year}-${pad.n2(month)}-${pad.n2(day)}T${pad.n2(hour)}:${pad.n2(minute)}:${pad.n2(second)}${offsetString}`;
    return Date.parse(dateString);
}
class Timezone {
    constructor(offsetHours) {
        this.offsetHours = offsetHours;
        this.offsetMs = offsetHours * exports.ONE_HOUR_MS;
        this.offsetString = `${Timezone.makeOffsetString(offsetHours)}:00`;
    }
    static makeOffsetString(offsetHours) {
        let prefix;
        if (offsetHours < 0) {
            prefix = '-';
            offsetHours = -offsetHours;
        }
        else {
            prefix = '+';
        }
        return `${prefix}${pad.n2(offsetHours)}`;
    }
    getSimpleDateFromDate(date) {
        return this.getSimpleDateFromMs(date.getTime());
    }
    getSimpleDateFromMs(ms) {
        return SimpleDate.fromUtc(new Date(ms + this.offsetMs));
    }
    getMsFromSimpleDate(date) {
        return date.utcDate.getTime() - this.offsetMs;
    }
    getDateFromSimpleDate(date) {
        return new Date(this.getMsFromSimpleDate(date));
    }
    getSimpleDateFromNow() {
        return this.getSimpleDateFromMs(Date.now());
    }
    getSimpleDateFromParts(year, month, day, hour = 0, minute = 0, second = 0) {
        return this.getSimpleDateFromMs(msFromParts(year, month, day, this.offsetString, hour, minute, second));
    }
    // A 2023-06-01
    getSimpleDateFromString(s) {
        const parts = s.split('-').map(Number);
        return this.getSimpleDateFromParts(parts[0], parts[1], parts[2]);
    }
    // Create a date which has the year/month/day in this timezone as `date` does when rendered in the
    // machine's current timezone.
    getSimpleDateFromLocalDate(date) {
        return this.getSimpleDateFromParts(date.getFullYear(), date.getMonth() + 1, date.getDate());
    }
}
exports.Timezone = Timezone;
function getSecondsFromTime(time) {
    const parts = time.split(':').map(parseFloat);
    while (parts.length < 3) {
        parts.push(0);
    }
    return 60 * (60 * parts[0] + parts[1]) + parts[2];
}
function compareNumbers(n1, n2) {
    if (n1 === n2)
        return 0;
    if (n1 < n2)
        return -1;
    return 1;
}
class SimpleDate {
    constructor(utcDate) {
        this.utcDate = utcDate;
    }
    static fromUtc(date) {
        return new SimpleDate(date);
    }
    static compare(d1, d2) {
        return compareNumbers(d1.utcDate.getTime(), d2.utcDate.getTime());
    }
    before(other) {
        return (compareNumbers(this.utcDate.getTime(), other.utcDate.getTime()) == -1);
    }
    after(other) {
        return compareNumbers(this.utcDate.getTime(), other.utcDate.getTime()) == 1;
    }
    getYear() {
        return this.utcDate.getUTCFullYear();
    }
    getMonth() {
        return this.utcDate.getUTCMonth() + 1;
    }
    getDate() {
        return this.utcDate.getUTCDate();
    }
    /**
     * 0 is Sunday
     * @returns
     */
    getDayOfWeekNumber() {
        return this.utcDate.getUTCDay();
    }
    isWeekStart() {
        return this.getDayOfWeekNumber() == exports.WEEK_START;
    }
    getHours() {
        return this.utcDate.getUTCHours();
    }
    getMinutes() {
        return this.utcDate.getUTCMinutes();
    }
    getSeconds() {
        return this.utcDate.getUTCSeconds();
    }
    getTime() {
        return `${pad.n2(this.utcDate.getUTCHours())}:${pad.n2(this.utcDate.getUTCMinutes())}`;
    }
    toString() {
        return `${this.getYear()}-${pad.n2(this.getMonth())}-${pad.n2(this.getDate())}`;
    }
    add(ms) {
        return SimpleDate.fromUtc(new Date(this.utcDate.getTime() + ms));
    }
    getDayStart() {
        const offset = this.utcDate.getUTCMilliseconds() +
            1000 *
                (this.utcDate.getUTCSeconds() +
                    60 *
                        (this.utcDate.getUTCMinutes() + 60 * this.utcDate.getUTCHours()));
        return SimpleDate.fromUtc(new Date(this.utcDate.getTime() - offset));
    }
    addSeconds(seconds) {
        return this.add(seconds * 1000);
    }
    addMinutes(minutes) {
        return this.addSeconds(minutes * 60);
    }
    addDays(days) {
        return this.add(days * exports.ONE_DAY_MS);
    }
    addWeeks(weeks) {
        return this.add(weeks * 7 * exports.ONE_DAY_MS);
    }
    /**
     * this - other in days
     *
     * @param other
     * @returns
     */
    diff(other) {
        return (this.utcDate.getTime() - other.utcDate.getTime()) / exports.ONE_DAY_MS;
    }
    getMonthBegin() {
        return SimpleDate.fromUtc(new Date(msFromParts(this.getYear(), this.getMonth(), 1, GMT_OFFSET_STRING)));
    }
    previousMonthBegin() {
        const [year, month] = previousMonth(this.getYear(), this.getMonth());
        return SimpleDate.fromUtc(new Date(msFromParts(year, month, 1, GMT_OFFSET_STRING)));
    }
    nextMonthBegin() {
        const [year, month] = nextMonth(this.getYear(), this.getMonth());
        return SimpleDate.fromUtc(new Date(msFromParts(year, month, 1, GMT_OFFSET_STRING)));
    }
}
exports.SimpleDate = SimpleDate;
function getWeekStart(date) {
    const pre_days = (date.getDayOfWeekNumber() - exports.WEEK_START + 7) % 7;
    return date.addDays(-pre_days).getDayStart();
}
// Returns the first day and day-after-the-last day to be shown in a calendar for a month.
// base-1 month.
function limitsOfMonth(year, month) {
    const base_0_month = month - 1;
    const first = SimpleDate.fromUtc(new Date(Date.UTC(year, base_0_month)));
    const begin = getWeekStart(first);
    let end = begin.addDays(28);
    if (end.getMonth() === month) {
        end = end.addDays(7);
    }
    return [begin, end];
}
// base-1 month.
function daysOfMonth(year, month) {
    return daysOf(...limitsOfMonth(year, month));
}
// begin <= d < end
function daysOf(begin, end) {
    const days = [];
    for (let d = begin; d.utcDate.getTime() < end.utcDate.getTime(); d = d.addDays(1)) {
        days.push(d);
    }
    return days;
}
// Groups into 7s
function weeksOf(days) {
    const weeks = [];
    for (let i = 0; i < days.length; i++) {
        if (i % 7 == 0) {
            weeks.push([]);
        }
        weeks[weeks.length - 1].push(days[i]);
    }
    return weeks;
}
// base-1 month.
function weeksOfMonth(year, month) {
    return weeksOf(daysOfMonth(year, month));
}
function nextMonth(year, month) {
    if (month == 12) {
        return [year + 1, 1];
    }
    else {
        return [year, month + 1];
    }
}
function previousMonth(year, month) {
    if (month == 1) {
        return [year - 1, 12];
    }
    else {
        return [year, month - 1];
    }
}
// Turn h:m:s into ms.
function makeTime(h, m, s) {
    h = h || 0;
    m = m || 0;
    s = s || 0;
    return 1000 * (s + 60 * (m + 60 * h));
}
// 23:00:00.000 -> 23*60*60*1000 ms
function parseTime(time) {
    let [h, m, s] = time.split(':');
    m = m || '0';
    s = s || '0';
    return 1000 * (parseFloat(s) + 60 * (parseInt(m) + 60 * parseInt(h)));
}
function parseTimeOrRange(times) {
    return times
        .split('-')
        .map(p => parseTime(p.trim()))
        .filter(p => !isNaN(p));
}
function parseTimesOrRanges(times) {
    return times
        .split(',')
        .map(p => parseTimeOrRange(p.trim()))
        .filter(p => p.length);
}
function groupByMonth(dates) {
    const by_month = new Map();
    for (const date of dates) {
        const key = `${date.getYear()}-${pad.n2(date.getMonth())}`;
        if (!by_month.has(key)) {
            by_month.set(key, []);
        }
        by_month.get(key)?.push(date);
    }
    const result = [];
    const entries = Array.from(by_month.entries());
    entries.sort();
    for (const entry of entries) {
        const dates = entry[1];
        dates.sort(SimpleDate.compare);
        result.push(dates);
    }
    return result;
}
/**
 * Duration of overlap between 2 time ranges in days.
 *
 * @param start1
 * @param end1
 * @param start2
 * @param end2
 * @returns
 */
function overlap(start1, end1, start2, end2) {
    if (start1.after(end2)) {
        return 0;
    }
    if (start2.after(end1)) {
        return 0;
    }
    // how much does 2 start after 1
    const missingStart = Math.max(0, start2.diff(start1));
    // how much does 2 end before 1
    const missingEnd = Math.max(0, end1.diff(end2));
    // Final amount is the duration of 1 minus how much is missing.
    return end1.diff(start1) - missingStart - missingEnd;
}
function lastOfMonth(date) {
    return date.getMonth() != date.addWeeks(1).getMonth();
}
